import Axios, { CancelTokenSource, AxiosRequestHeaders } from "axios";
import config from "../config/config";
import { FormData } from "../contexts/interfaces";
import { msalInstance } from "../index";
import { loginRequest } from "../config/authConfig";

//const serverUrl = process.env.REACT_APP_SERVER_URL;
const serverUrl = config.serverUrl;

export const getHeaders = async (): Promise<AxiosRequestHeaders> => {
  console.log("=============getting headers");
  const account = msalInstance.getActiveAccount();
  if (!account) {
    throw Error(
      "No active account! Verify a user has been signed in and setActiveAccount has been called."
    );
  }

  const response = await msalInstance.acquireTokenSilent({
    ...loginRequest,
    account: account,
  });

  const headers = {} as AxiosRequestHeaders;
  const bearer = `Bearer ${response.accessToken}`;

  headers.Authorization = bearer;
  // console.log('==========BEARER', bearer);
  return headers;
};

// Approves form, setting it's state to "approved"
const approveForm = async (
  userId: number,
  formId: number,
  employeeId: number,
  replacePrevious: boolean,
  firstName: string,
  lastName: string,
  doctorUniqueIdCode: string | null
) => {
  try {
    const headers = await getHeaders();
    const res = await Axios.post(
      `${serverUrl}/api/approveForm`,
      {
        userId,
        formId,
        employeeId,
        replacePrevious,
        dbFirstName: firstName,
        dbLastName: lastName,
        doctorUniqueIdCode,
      },
      {
        headers,
      }
    );

    return res;
  } catch (err: any) {
    return err;
  }
};

// Gets data for all employees for selected unit (e.g. in main view)
// or by employeeId (e.g. in profile)
const getEmployee = async (
  userId: number,
  unitId: number | undefined,
  employeeId: number | undefined,
  formId: number | null,
  source: CancelTokenSource
) => {
  try {
    const headers = await getHeaders();
    const res = await Axios.post(
      `${serverUrl}/api/search`,
      {
        userId,
        unitId,
        employeeId,
        formId,
      },
      {
        headers,
        cancelToken: source.token,
      }
    );
    console.log(res.data);
    return res;
  } catch (err: any) {
    return err;
  }
};

const getForm = async (
  userId: number,
  employeeId: number | null,
  formStatus: string,
  formId: number | null,
  source: CancelTokenSource
) => {
  try {
    const headers = await getHeaders();
    const res = await Axios.post(
      `${serverUrl}/api/getForm`,
      {
        userId,
        employeeId,
        formStatus,
        formId,
      },
      {
        headers,
        cancelToken: source.token,
      }
    );
    console.log(res);
    return res;
  } catch (err: any) {
    return err;
  }
};

const getSelectableEducationLevels = async (userId: number) => {
  try {
    const headers = await getHeaders();
    const res = await Axios.post(
      `${serverUrl}/api/selectableEducationLevel`,
      {
        userId,
      },
      {
        headers,
      }
      // { cancelToken: source.token }
    );
    console.log(res.data.result);
    return res;
  } catch (err: any) {
    return err;
  }
};

const getSelectableDoctors = async (userId: number, expenseUnitId: number) => {
  try {
    const headers = await getHeaders();
    const res = await Axios.post(
      `${serverUrl}/api/getSelectableDoctors`,
      {
        userId,
        expenseUnitId,
      },
      {
        headers,
      }
      // { cancelToken: source.token }
    );
    console.log(res.data.result);
    return res;
  } catch (err: any) {
    return err;
  }
};

const getSelectablePermits = async (userId: number, educationId?: number) => {
  try {
    const headers = await getHeaders();
    const res = await Axios.post(
      `${serverUrl}/api/selectablePermits`,
      {
        userId,
        educationId,
      },
      {
        headers,
      }
      // { cancelToken: source.token }
    );
    console.log(res.data.result);
    return res;
  } catch (err: any) {
    return err;
  }
};

const getRequiredAccomplishments = async (userId: number) => {
  try {
    const headers = await getHeaders();
    const res = await Axios.post(
      `${serverUrl}/api/getRequiredAccomplishments`,
      {
        userId,
      },
      {
        headers,
      }
      // { cancelToken: source.token }
    );
    console.log(res.data.result);
    return res;
  } catch (err: any) {
    return err;
  }
};

const login = async () => {
  try {
    const headers = await getHeaders();
    const res = await Axios.post(
      `${serverUrl}/api/login`,
      {},
      {
        headers,
      }
    );
    console.log(res);
    return res;
  } catch (err) {
    console.log(err);
  }
};

// Update form
const updateForm = async (
  userId: number,
  employeeId: number,
  formId: number | null | undefined,
  currentData: FormData
) => {
  try {
    console.log("Current before save: ", currentData);
    const headers = await getHeaders();
    const res = await Axios.post(
      `${serverUrl}/api/updateForm`,
      {
        userId,
        employeeId,
        formId,
        currentData,
      },
      {
        headers,
      }
    );
    console.log(res);
    return res;
  } catch (err: any) {
    console.log(err);
    return err;
  }
};

// Returns form back, setting status back to "approval"
const returnBackForm = async (
  userId: number,
  formId: number,
  employeeId: number,
  input: string,
  doctorNameString: string
) => {
  try {
    const headers = await getHeaders();
    const res = await Axios.post(
      `${serverUrl}/api/returnFormToApproval`,
      {
        userId,
        formId,
        employeeId,
        input,
        doctorNameString,
      },
      {
        headers,
      }
    );
    return res;
  } catch (err: any) {
    return err;
  }
};

// Change checked status of form in doctor view
const changeCheckedToDB = async (
  formId: number | undefined,
  checkedStatus: boolean
) => {
  try {
    if (formId === undefined || isNaN(formId)) {
      throw Error("Invalid FormId");
    }
    const headers = await getHeaders();
    await Axios.post(
      `${serverUrl}/api/changeCheckedStatus`,
      {
        formId,
        checkedStatus,
      },
      {
        headers,
      }
    );
  } catch (err: any) {
    return err;
  }
};

// Remove permits
const removePermitsByIds = async (
  userId: number,
  employeeId: number,
  permitIds: number[]
) => {
  try {
    const headers = await getHeaders();
    const res = await Axios.post(
      `${serverUrl}/api/removePermitsByIds`,
      {
        userId,
        employeeId,
        permitIds,
      },
      {
        headers,
      }
    );
    return res;
  } catch (err: any) {
    return err;
  }
};

const searchEmployees = async (
  userId: number,
  unitIds: number[],
  source: CancelTokenSource
) => {
  try {
    const headers = await getHeaders();
    const res = await Axios.post(
      `${serverUrl}/api/searchAllEmployees`,
      {
        userId,
        unitIds,
      },
      {
        headers,
        cancelToken: source.token,
      }
    );
    return res;
  } catch (err: any) {
    return err;
  }
};

// Get all expense units
const getExpenseUnits = async (source: CancelTokenSource) => {
  try {
    const headers = await getHeaders();
    const res = await Axios.post(
      `${serverUrl}/api/getExpenseUnits`,
      {},
      {
        headers,
        cancelToken: source.token,
      }
    );
    return res;
  } catch (err: any) {
    return err;
  }
};

// Upsert expense unit (add or update)
const upsertExpenseUnit = async (id: number, name: string) => {
  try {
    const headers = await getHeaders();
    const res = await Axios.post(
      `${serverUrl}/api/upsertExpenseUnit`,
      {
        id,
        name,
      },
      {
        headers,
      }
    );
    return res;
  } catch (err: any) {
    return err;
  }
};

// Get system config by key
const getSystemConfig = async (key: string) => {
  try {
    const headers = await getHeaders();
    const res = await Axios.post(
      `${serverUrl}/api/getSystemConfig`,
      {
        key,
      },
      {
        headers,
      }
    );
    return res;
  } catch (err: any) {
    return err;
  }
};

// Update system config by key
const updateSystemConfig = async (key: string, value: string) => {
  try {
    const headers = await getHeaders();
    const res = await Axios.post(
      `${serverUrl}/api/updateSystemConfig`,
      {
        key,
        value,
      },
      {
        headers,
      }
    );
    return res;
  } catch (err: any) {
    return err;
  }
};

export {
  approveForm,
  changeCheckedToDB,
  getEmployee,
  getForm,
  getSelectableEducationLevels,
  getSelectableDoctors,
  getSelectablePermits,
  getRequiredAccomplishments,
  login,
  returnBackForm,
  removePermitsByIds,
  searchEmployees,
  updateForm,
  getExpenseUnits,
  upsertExpenseUnit,
  getSystemConfig,
  updateSystemConfig,
};
